import { BottomSheetBackdrop, BottomSheetModal } from '@gorhom/bottom-sheet';
import type {
  BottomSheetBackdropProps,
  BottomSheetProps
} from '@gorhom/bottom-sheet';
import type { BackdropPressBehavior } from '@gorhom/bottom-sheet/lib/typescript/components/bottomSheetBackdrop/types';
import type { ComponentType, FC, PropsWithChildren, ReactNode } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import type { ScrollViewProps, TextStyle } from 'react-native';
import { StyleSheet, View, useWindowDimensions } from 'react-native';
import type { IconButtonProps } from 'react-native-paper';
import { Text } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BottomModalEndButtons } from '@/features/popup/components/BottomModalEndButtons';
import type { BottomModalEndButton } from '@/features/popup/components/BottomModalEndButtons';
import BottomSheetKeyboardAwareScrollView from '@/features/popup/components/BottomSheetKeyboardAwareScrollView';
import { useBottomModalPresentation } from '@/features/popup/hooks/useBottomModalPresentation';
import { isWeb } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const SHEET_MAX_HEIGHT_RATIO = 0.9;

export type {
  BottomModalEndButton,
  BottomModalEndButtonVariant
} from '@/features/popup/components/BottomModalEndButtons';

const makeStyles = (theme: AppTheme, maxWidth?: number) => {
  const sheetStyle = {
    flex: 1,
    backgroundColor: theme.colors.surfaceContainerLow
  };
  const webSheetStyle =
    isWeb && maxWidth
      ? ({
          width: '100%',
          maxWidth,
          marginHorizontal: 'auto',
          backgroundColor: theme.colors.surfaceContainerLow,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.15)',
          elevation: 8
        } as const)
      : undefined;

  return StyleSheet.create({
    sheet: sheetStyle,
    sheetContent: webSheetStyle ?? sheetStyle,
    header: {
      backgroundColor: theme.colors.surfaceContainerLow,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0
    },
    headerRow: {
      flexDirection: 'row',
      padding: 12,
      alignItems: 'center',
      justifyContent: 'center'
    },
    title: { flex: 1 },
    closeIcon: {
      position: 'absolute',
      right: 12,
      top: 6,
      flexDirection: 'row'
    }
  });
};

/**
 * Children should not be wrapped inside a scroll view
 */
export type BottomModalProps = {
  isVisible?: boolean;
  title?: string;
  titleAlign?: TextStyle['textAlign'];
  offsetCloseButton?: boolean;
  onClose?: () => void;
  showCloseButton?: boolean;
  closeButtonMode?: IconButtonProps['mode'];
  enablePanDownToClose?: boolean;
  endButtons?: BottomModalEndButton[];
  /** Optional fixed snap points for content that should not size to its children. */
  snapPoints?: (string | number)[];
  /** Defaults to the bottom sheet's dynamic sizing when omitted. */
  enableDynamicSizing?: boolean;
  /** Optional Reanimated configuration for sheet position and size changes. */
  animationConfigs?: BottomSheetProps['animationConfigs'];
  /** Maximum dynamic sheet height as a fraction of the available screen height. */
  maxDynamicContentSizeRatio?: number;
  /** Whether the scroll content should fill the sheet when it is shorter. */
  fillScrollContent?: boolean;
  /** Keep the title and action buttons visible while scrolling. */
  stickyHeader?: boolean;
  /**
   * Opt-in: enables web-specific modal styling when set.
   * - Centers the modal horizontally on web
   * - Constrains width to this value
   * - Adds shadow + border radius on web
   * - Makes outer sheet background transparent on web
   * Has no effect on native (iOS/Android).
   */
  maxWidth?: number;
  expandWithKeyboard?: boolean;
  scrollComponent?: ComponentType<
    Pick<
      ScrollViewProps,
      | 'contentContainerStyle'
      | 'keyboardShouldPersistTaps'
      | 'style'
      | 'stickyHeaderIndices'
    > & {
      children: ReactNode;
    }
  >;
  keyboardShouldPersistTaps?: ScrollViewProps['keyboardShouldPersistTaps'];
  backdropPressBehavior?: BackdropPressBehavior;
  /** Handle backdrop taps without dismissing the sheet automatically. */
  onBackdropPress?: () => void;
} & PropsWithChildren;

export const BottomModal: FC<BottomModalProps> = ({
  isVisible = false,
  title,
  titleAlign = 'center',
  offsetCloseButton = false,
  onClose,
  showCloseButton = true,
  enablePanDownToClose = true,
  closeButtonMode,
  endButtons,
  snapPoints,
  enableDynamicSizing,
  animationConfigs,
  maxDynamicContentSizeRatio = SHEET_MAX_HEIGHT_RATIO,
  fillScrollContent = true,
  stickyHeader = false,
  maxWidth,
  expandWithKeyboard = false,
  scrollComponent: ScrollComponent = BottomSheetKeyboardAwareScrollView,
  keyboardShouldPersistTaps,
  backdropPressBehavior = 'close',
  onBackdropPress,
  children
}) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const { height } = useWindowDimensions();
  const styles = useMemo(() => makeStyles(theme, maxWidth), [maxWidth, theme]);
  const maxDynamicContentSize =
    (height - insets.top) * maxDynamicContentSizeRatio;

  const { bottomSheetModalRef, handleClose, handleDismiss } =
    useBottomModalPresentation({ isVisible, onClose });
  const isHandlingBackdropPressRef = useRef(false);

  const handleBackdropPress = useCallback(() => {
    if (!onBackdropPress || isHandlingBackdropPressRef.current) return;

    isHandlingBackdropPressRef.current = true;
    onBackdropPress();
    requestAnimationFrame(() => {
      isHandlingBackdropPressRef.current = false;
    });
  }, [onBackdropPress]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        opacity={0.5}
        // The backdrop only installs its tap gesture when pressBehavior is not
        // 'none'. These modals have a single snap point, so snapping to 0 keeps
        // the sheet open while onBackdropPress handles closing/confirmation.
        pressBehavior={onBackdropPress ? 0 : backdropPressBehavior}
        onPress={onBackdropPress ? handleBackdropPress : undefined}
      />
    ),
    [backdropPressBehavior, handleBackdropPress, onBackdropPress]
  );

  const endbuttons = useMemo(
    () => (
      <BottomModalEndButtons
        showCloseButton={showCloseButton}
        closeButtonMode={closeButtonMode}
        offsetCloseButton={offsetCloseButton}
        endButtons={endButtons}
        onClose={handleClose}
        theme={theme}
      />
    ),
    [
      closeButtonMode,
      endButtons,
      handleClose,
      offsetCloseButton,
      showCloseButton,
      theme
    ]
  );

  const content = useMemo(
    () => [
      <View key="header" style={stickyHeader ? styles.header : undefined}>
        {offsetCloseButton && (
          <View style={{ paddingHorizontal: 16 }}>{endbuttons}</View>
        )}
        <View style={styles.headerRow}>
          <Text
            variant="headlineLarge"
            style={[styles.title, { textAlign: titleAlign }]}
          >
            {title}
          </Text>
          {!offsetCloseButton && (
            <View style={styles.closeIcon}>{endbuttons}</View>
          )}
        </View>
      </View>,
      <View
        key="body"
        style={{ paddingBottom: isWeb ? 16 + insets.bottom : insets.bottom }}
      >
        {children}
      </View>
    ],
    [
      insets.bottom,
      children,
      endbuttons,
      offsetCloseButton,
      styles.closeIcon,
      styles.headerRow,
      styles.header,
      styles.title,
      title,
      stickyHeader,
      titleAlign
    ]
  );

  return (
    <BottomSheetModal
      ref={bottomSheetModalRef}
      onDismiss={handleDismiss}
      enablePanDownToClose={enablePanDownToClose}
      snapPoints={snapPoints}
      enableDynamicSizing={enableDynamicSizing}
      animationConfigs={animationConfigs}
      // Android + New Architecture: the content pan gesture (used to drag the
      // sheet from its scrollable content) conflicts with the ScrollView's
      // native scroll gesture and blocks scrolling entirely. Disabling it
      // keeps the handle drag/pan-down-to-close working while letting the
      // BottomSheetScrollView scroll normally.
      enableContentPanningGesture={false}
      keyboardBlurBehavior="restore"
      keyboardBehavior={expandWithKeyboard ? 'interactive' : undefined}
      maxDynamicContentSize={maxDynamicContentSize}
      enableBlurKeyboardOnGesture
      backgroundStyle={
        isWeb && maxWidth ? { backgroundColor: 'transparent' } : styles.sheet
      }
      style={isWeb && maxWidth ? styles.sheetContent : undefined}
      topInset={insets.top}
      handleIndicatorStyle={{
        backgroundColor: enablePanDownToClose
          ? theme.colors.outline
          : 'transparent'
      }}
      backdropComponent={renderBackdrop}
    >
      <ScrollComponent
        style={styles.sheet}
        contentContainerStyle={
          isWeb || fillScrollContent ? styles.sheetContent : undefined
        }
        keyboardShouldPersistTaps={keyboardShouldPersistTaps}
        stickyHeaderIndices={stickyHeader ? [0] : undefined}
      >
        {content}
      </ScrollComponent>
    </BottomSheetModal>
  );
};
